// Epoch 17: CSR Lead Response Form Component
// Form for CSRs to submit LinkedIn lead responses
// Date: July 25, 2025

import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../supabase/client';
import { Campaign } from '../../types';
import { CSRLeadResponseForm as FormData, LeadResponse, ConversationMessage, OutreachTeamMember, ParsingCorrection } from '../../types/lead-responses';
// EPOCH 21 PHASE 3: Import validation components
import CSRParsingValidationInterface from './CSRParsingValidationInterface';
import ParsingPreviewPanel from './ParsingPreviewPanel';
import CampaignSearchSelect from '../common/CampaignSearchSelect';
import { extractEdgeFunctionErrorMessage } from '../../utils/edgeFunctionError';
import { getDraftStorageKey, LEGACY_UNSCOPED_FOLLOWUP_DRAFT_KEY } from '../../utils/csrDraftStorage';

interface CSRLeadResponseFormProps {
  mode?: 'new' | 'followup'; // EPOCH 29: Support both new and follow-up responses
  parentResponse?: LeadResponse; // EPOCH 29: Parent response for follow-up mode
  onSuccess?: (leadResponse: LeadResponse) => void;
  onCancel?: () => void;
}

const CSRLeadResponseForm: React.FC<CSRLeadResponseFormProps> = ({
  mode = 'new', // EPOCH 29: Default to new response mode
  parentResponse, // EPOCH 29: Parent response for follow-ups
  onSuccess,
  onCancel
}) => {
  // EPOCH 29: Validate props
  useEffect(() => {
    if (mode === 'followup' && !parentResponse) {
      console.error('Parent response required for follow-up mode');
    }
  }, [mode, parentResponse]);

  const [formData, setFormData] = useState<FormData>({
    // EPOCH 29: Initialize based on mode
    campaign_id: mode === 'followup' && parentResponse ? parentResponse.campaign_id : '',
    lead_name: mode === 'followup' && parentResponse ? parentResponse.lead_name : '',
    lead_linkedin_url: mode === 'followup' && parentResponse ? parentResponse.lead_linkedin_url || '' : '',
    lead_company: mode === 'followup' && parentResponse ? parentResponse.lead_company || '' : '',
    lead_title: mode === 'followup' && parentResponse ? parentResponse.lead_title || '' : '',
    lead_location: mode === 'followup' && parentResponse ? parentResponse.lead_location || '' : '',
    alias_profile: mode === 'followup' && parentResponse ? parentResponse.alias_profile : '',
    conversation_raw: '', // Always empty for new conversation input
    csr_notes: mode === 'followup' && parentResponse ? parentResponse.csr_notes || '' : '',
    // Post-Epoch 22: Additional Contact Fields
    lead_email: mode === 'followup' && parentResponse ? parentResponse.lead_email || '' : '',
    lead_phone: mode === 'followup' && parentResponse ? parentResponse.lead_phone || '' : '',
    lead_website: mode === 'followup' && parentResponse ? parentResponse.lead_website || '' : ''
  });

  // EPOCH 60: Extended campaign interface with company info
  // EPOCH 75: Added target_list_name
  interface CampaignWithCompany {
    id: string;
    name: string;
    status: string;
    assigned_csr: string;
    updated_at: string;
    company_name: string;
    sequence_id?: string;
    target_list_name?: string;
    dripify_names?: string[];
  }

  // EPOCH 70: Message from campaign sequence
  interface SequenceMessage {
    message_number: number;
    content: string;
    character_count: number;
    delay_days?: number;
  }

  const [campaigns, setCampaigns] = useState<CampaignWithCompany[]>([]);
  const [recentCampaignIds, setRecentCampaignIds] = useState<string[]>([]); // EPOCH 60: Recently used campaigns
  const [loading, setLoading] = useState(false);

  // EPOCH 70: Analytics tracking state
  const [campaignMessages, setCampaignMessages] = useState<SequenceMessage[]>([]);
  const [sequenceMessageNumber, setSequenceMessageNumber] = useState<number | undefined>(undefined);
  const [responseTrigger, setResponseTrigger] = useState<string>('drip_sequence');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [autoSaveStatus, setAutoSaveStatus] = useState<'saved' | 'saving' | 'unsaved'>('saved');
  
  // EPOCH 21 PHASE 3: Validation interface state
  const [showValidationInterface, setShowValidationInterface] = useState(false);
  const [showParsingPreview, setShowParsingPreview] = useState(true);
  const [teamMembers, setTeamMembers] = useState<OutreachTeamMember[]>([]);
  const [parsedMessages, setParsedMessages] = useState<ConversationMessage[]>([]);
  const [validationCorrections, setValidationCorrections] = useState<ParsingCorrection[]>([]);
  
  // EPOCH 51: Alias dropdown options
  const [aliasOptions, setAliasOptions] = useState<Array<{ value: string; label: string; teamMemberName: string }>>([]);
  const [loadingAliases, setLoadingAliases] = useState(false);

  // Message-limit config (2026-10-12): the approaching-limit warning previously used a
  // hardcoded ">= 8" / "10 messages maximum" with no awareness of the real per-company
  // config (company_messaging_config.warning_at_message_count / max_message_exchanges, which
  // every real company is actually configured to 90 / 50-exchanges-i.e.-100-total). Mirrors the
  // same fetch + fallback pattern already used in LeadResponseDetail.tsx so both surfaces agree.
  const [messagingConfig, setMessagingConfig] = useState<{
    max_message_exchanges: number;
    warning_at_message_count: number;
  } | null>(null);

  // Dripify smart paste state
  const [dripifyPaste, setDripifyPaste] = useState('');
  const [dripifyLinkedInUrl, setDripifyLinkedInUrl] = useState('');
  const [dripifyParseResult, setDripifyParseResult] = useState<{ filled: string[]; missing: string[] } | null>(null);
  const [showDripifyPanel, setShowDripifyPanel] = useState(true);

  // EPOCH 21 PHASE 3 & 51: Load outreach team members for validation and dropdown
  const loadTeamMembers = async () => {
    setLoadingAliases(true);
    try {
      const { data, error } = await supabase.functions.invoke('outreach-team-config', {
        body: { action: 'list' }
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      const members = data.data || [];
      setTeamMembers(members);
      
      // EPOCH 51: Build alias dropdown options
      const options: Array<{ value: string; label: string; teamMemberName: string }> = [];
      
      members.forEach((member: OutreachTeamMember) => {
        if (!member.is_active) return; // Skip inactive members
        
        // Add primary team member name
        options.push({
          value: member.team_member_name,
          label: member.team_member_name,
          teamMemberName: member.team_member_name
        });
        
        // Add all aliases
        if (member.aliases && member.aliases.length > 0) {
          member.aliases.forEach((alias: any) => {
            // Skip if it's the same as the team member name
            if (alias.alias_name !== member.team_member_name) {
              options.push({
                value: alias.alias_name,
                label: `${alias.alias_name} (${member.team_member_name})`,
                teamMemberName: member.team_member_name
              });
            }
          });
        }
      });
      
      setAliasOptions(options);
      console.log('✅ EPOCH 51: Loaded alias options:', options);
    } catch (error) {
      console.warn('❌ Failed to load team members:', error);
      // Use fallback team members if API fails
      const fallbackMembers = [
        {
          id: 'fallback-1',
          team_member_name: 'Phoebe',
          aliases: [{ alias_name: 'Phoebe', is_primary: true }]
        },
        {
          id: 'fallback-2', 
          team_member_name: 'Lilly',
          aliases: [{ alias_name: 'Lilly', is_primary: true }]
        }
      ];
      setTeamMembers(fallbackMembers);
      
      // Build fallback options
      setAliasOptions([
        { value: 'Phoebe', label: 'Phoebe', teamMemberName: 'Phoebe' },
        { value: 'Lilly', label: 'Lilly', teamMemberName: 'Lilly' }
      ]);
    } finally {
      setLoadingAliases(false);
    }
  };

  // EPOCH 21 PHASE 3: Handle validation completion
  const handleParsingValidated = useCallback(async (validatedMessages: ConversationMessage[], corrections: ParsingCorrection[]) => {
    setParsedMessages(validatedMessages);
    setValidationCorrections(corrections);
    setShowValidationInterface(false);
    
    // Update the conversation_parsed field in the preview
    setFormData(prev => ({
      ...prev,
      conversation_parsed: validatedMessages
    }));
    
    console.log('✅ EPOCH 22: Parsing validated with', corrections.length, 'corrections, validated messages:', validatedMessages.length);
    console.log('📝 Validated parsing will be saved when form is submitted');
  }, []);

  // EPOCH 21 PHASE 3: Handle parsing preview updates
  const handleParsingPreviewUpdate = useCallback((messages: ConversationMessage[]) => {
    // Don't override AI-validated messages with empty rule-based parsing
    if (parsedMessages.length > 0 && messages.length === 0) {
      console.log('🚫 BLOCKED: Preventing empty messages from overwriting AI-validated messages');
      return;
    }
    
    setParsedMessages(messages);
  }, [parsedMessages]);

  // EPOCH 60: Load campaigns with company information for better dropdown display
  const loadCampaigns = async () => {
    try {
      // Get current user to check their role
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      // CSRs should see campaigns that are assigned to them
      // Show both submitted (awaiting approval) and running campaigns
      // EPOCH 60: Use direct campaigns.company_id → companies join (same pattern as CSRCampaigns.tsx)
      const { data: campaignsData, error: campaignsError } = await supabase
        .from('campaigns')
        .select(`
          id,
          name,
          status,
          assigned_csr,
          updated_at,
          company_id,
          sequence_id,
          dripify_names,
          companies:company_id (
            id,
            name
          )
        `)
        .eq('assigned_csr', session.user.id)
        .in('status', ['submitted', 'running']);

      if (campaignsError) {
        console.error('Error loading campaigns:', campaignsError);
        setCampaigns([]);
        return;
      }

      if (!campaignsData || campaignsData.length === 0) {
        console.log('📋 EPOCH 60: No campaigns found for CSR');
        setCampaigns([]);
        return;
      }

      // Epoch 75: Separate query for preset_filter_requests.
      // Query by company_id (not campaign_id) to match the CSR RLS policy which
      // checks company_csrs.company_id rather than campaign ownership.
      const companyIds = [...new Set(campaignsData.map((c: any) => c.company_id).filter(Boolean))];
      let filterRequestMap: Record<string, string> = {};
      if (companyIds.length > 0) {
        const { data: filterData } = await supabase
          .from('preset_filter_requests')
          .select('campaign_id, request_name')
          .in('company_id', companyIds);
        (filterData || []).forEach((r: any) => {
          if (r.request_name) filterRequestMap[r.campaign_id] = r.request_name;
        });
      }

      // EPOCH 60: Process and sort campaigns by company name, then by activity
      const processedCampaigns: CampaignWithCompany[] = campaignsData.map((campaign: any) => ({
        id: campaign.id,
        name: campaign.name,
        status: campaign.status,
        assigned_csr: campaign.assigned_csr,
        updated_at: campaign.updated_at,
        company_name: campaign.companies?.name || 'Unknown Company',
        sequence_id: campaign.sequence_id || undefined,
        target_list_name: filterRequestMap[campaign.id] || undefined,
        dripify_names: campaign.dripify_names || [],
      }));

      // Sort by company name (A-Z), then by updated_at (newest first)
      processedCampaigns.sort((a, b) => {
        const companyCompare = a.company_name.localeCompare(b.company_name);
        if (companyCompare !== 0) return companyCompare;
        return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
      });

      console.log('📋 EPOCH 60: Available campaigns for CSR (with company):', processedCampaigns);
      setCampaigns(processedCampaigns);
    } catch (error) {
      console.error('Error loading campaigns:', error);
    }
  };

  // EPOCH 60: Load recently used campaigns (last 5 campaigns CSR submitted lead responses to)
  const loadRecentCampaigns = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const { data, error } = await supabase
        .from('lead_responses')
        .select('campaign_id, created_at')
        .eq('submitted_by', session.user.id)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error loading recent campaigns:', error);
        return;
      }

      // Get unique campaign IDs, most recent first, limit to 5
      const uniqueIds: string[] = [];
      for (const row of data || []) {
        if (row.campaign_id && !uniqueIds.includes(row.campaign_id)) {
          uniqueIds.push(row.campaign_id);
          if (uniqueIds.length >= 5) break;
        }
      }

      console.log('📌 EPOCH 60: Recently used campaigns:', uniqueIds);
      setRecentCampaignIds(uniqueIds);
    } catch (error) {
      console.error('Error loading recent campaigns:', error);
    }
  };

  // Load the real per-company message-limit config, falling back to the same defaults
  // LeadResponseDetail.tsx uses when no config row exists for the company (should not happen
  // in practice -- every current company has a real row -- but kept for safety/new companies).
  const loadMessagingConfig = async (companyId: string) => {
    try {
      const { data, error } = await supabase
        .from('company_messaging_config')
        .select('max_message_exchanges, warning_at_message_count')
        .eq('company_id', companyId)
        .maybeSingle();

      if (error || !data) {
        setMessagingConfig({ max_message_exchanges: 5, warning_at_message_count: 8 });
      } else {
        setMessagingConfig(data);
      }
    } catch (err) {
      console.error('Error loading messaging config:', err);
      setMessagingConfig({ max_message_exchanges: 5, warning_at_message_count: 8 });
    }
  };

  // EPOCH 70: Load campaign message sequence when campaign changes
  const loadCampaignMessages = async (campaignId: string) => {
    setCampaignMessages([]);
    setSequenceMessageNumber(undefined);

    try {
      // Query the campaign directly to get sequence_id (don't rely on local state)
      const { data: campaignData, error: campaignError } = await supabase
        .from('campaigns')
        .select('sequence_id')
        .eq('id', campaignId)
        .single();

      if (campaignError || !campaignData?.sequence_id) {
        console.log('EPOCH 70: No sequence_id found for campaign');
        return;
      }

      const { data, error } = await supabase
        .from('campaign_sequences')
        .select('messages')
        .eq('id', campaignData.sequence_id)
        .single();

      if (error || !data?.messages) {
        console.log('EPOCH 70: No sequence messages found for campaign');
        return;
      }

      const messages = (data.messages as SequenceMessage[]).sort(
        (a, b) => a.message_number - b.message_number
      );
      setCampaignMessages(messages);
      console.log('EPOCH 70: Loaded', messages.length, 'sequence messages for campaign');
    } catch (err) {
      console.warn('EPOCH 70: Error loading campaign messages:', err);
    }
  };

  // EPOCH 70: Auto-detect response_trigger for follow-up mode
  useEffect(() => {
    if (mode !== 'followup' || !parentResponse) {
      setResponseTrigger('drip_sequence');
      return;
    }

    const detectTrigger = async () => {
      try {
        // Check if parent has recent reply requests that were sent
        const { data: replyRequests } = await supabase
          .from('reply_requests')
          .select('id, sent_at, is_sequence_continuation, created_at')
          .eq('lead_response_id', parentResponse.id)
          .not('sent_at', 'is', null)
          .order('sent_at', { ascending: false })
          .limit(1);

        if (replyRequests && replyRequests.length > 0) {
          const latestReply = replyRequests[0];
          if (latestReply.is_sequence_continuation) {
            setResponseTrigger('sequence_continuation');
            console.log('EPOCH 70: Auto-detected trigger: sequence_continuation');
          } else {
            setResponseTrigger('reply_request');
            console.log('EPOCH 70: Auto-detected trigger: reply_request');
          }
        } else {
          setResponseTrigger('drip_sequence');
          console.log('EPOCH 70: Auto-detected trigger: drip_sequence (no sent replies found)');
        }
      } catch (err) {
        console.warn('EPOCH 70: Error detecting response trigger:', err);
        setResponseTrigger('drip_sequence');
      }
    };

    detectTrigger();
  }, [mode, parentResponse]);

  // Dripify smart paste parser — extracts lead fields from a copy-pasted Dripify lead page
  const parseDripifyBlob = (blob: string) => {
    // Normalize line endings (Dripify copy-paste may produce \r\n on Windows)
    const text = blob.replace(/\r\n/g, '\n').replace(/\r/g, '\n');

    // Split on "Type your message here" — everything before is the conversation, after is the profile sidebar
    const typeHereIdx = text.indexOf('Type your message here');
    const conversationSection = typeHereIdx !== -1 ? text.substring(0, typeHereIdx) : text;
    const profileSection = typeHereIdx !== -1 ? text.substring(typeHereIdx) : '';

    // Lead name: in the Dripify inbox sidebar, the selected lead always appears as
    // "NAME\nNAME\n[date DD.MM.YYYY or time HH:MM]" (name repeated twice, then a date or time).
    // Today's conversations show a time (e.g. "02:37"), older ones show a date (e.g. "06.07.2026").
    // The Dripify account owner appears as "Ivy Lin\n\nIvy Lin" with a blank line — no date/time follows.
    const inboxItemMatch = text.match(/([^\n]+)\n\1\n(?:\d{2}\.\d{2}\.\d{4}|\d{2}:\d{2})/);
    const lead_name = inboxItemMatch?.[1]?.trim() || undefined;

    // Email: find email address pattern anywhere in profile section
    const emailMatch = profileSection.match(/([a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,})/);
    const lead_email = emailMatch?.[1];

    // Label-based extraction — looks for "LabelWord\n\nValue" or "LabelWord\nValue"
    const extractAfterLabel = (label: string): string | undefined => {
      const regex = new RegExp(`\\b${label}\\b\\s*\\n+([^\\n]{2,})`);
      const m = profileSection.match(regex);
      // Filter out meta-strings like "Find email", "Add email address", "Go to lead's page"
      const value = m?.[1]?.trim();
      if (!value || /^(Find|Add|Go to|Campaigns)/i.test(value)) return undefined;
      return value;
    };

    const lead_company = extractAfterLabel('Company');
    const lead_title = extractAfterLabel('Position');
    const city = extractAfterLabel('City');
    const country = extractAfterLabel('Country');
    const lead_location = [city, country].filter(Boolean).join(', ') || undefined;
    const lead_phone = extractAfterLabel('Phone Number');

    // Dripify campaign name: line(s) after "Campaigns:" label
    const campaignMatch = profileSection.match(/Campaigns?:\s*\n+([^\n]+)/);
    const dripify_campaign = campaignMatch?.[1]?.trim();

    // Conversation: from the first date header to end of conversation section
    const dateMatch = conversationSection.match(/((?:Mon|Tue|Wed|Thu|Fri|Sat|Sun)\s+\w+\s+\d{1,2}\s+\d{4})/);
    const conversation_raw = dateMatch?.index !== undefined
      ? conversationSection.substring(dateMatch.index).trim()
      : conversationSection.trim() || undefined;

    // Alias detection: scan conversation for "Hi [Name]," (lead addressing alias)
    // and sign-offs in outbound messages.
    // Use [A-Za-z]+ (not \w+) to stop before merged timestamps like "Vanessa01:24 am".
    const candidateNames: string[] = [];
    for (const m of (conversation_raw || '').matchAll(/\bHi ([A-Za-z]+)[,!]/g)) candidateNames.push(m[1]);
    for (const m of (conversation_raw || '').matchAll(/\bHey ([A-Za-z]+)[,!]/g)) candidateNames.push(m[1]);
    for (const m of (conversation_raw || '').matchAll(/Thanks[!,]?\s*\n([A-Za-z]+)/gm)) candidateNames.push(m[1]);
    for (const m of (conversation_raw || '').matchAll(/\n([A-Za-z]+)\nhttps?:\/\//gm)) candidateNames.push(m[1]);
    const alias_profile = aliasOptions.find(opt =>
      candidateNames.some(n => n.toLowerCase() === opt.value.toLowerCase())
    )?.value;

    return { lead_name, lead_email, lead_company, lead_title, lead_location, lead_phone, dripify_campaign, conversation_raw, alias_profile };
  };

  const handleDripifyParse = () => {
    const parsed = parseDripifyBlob(dripifyPaste);
    const filled: string[] = [];
    const missing: string[] = [];
    const updates: Partial<FormData> = {};

    if (parsed.lead_name) { updates.lead_name = parsed.lead_name; filled.push('Name'); }
    else missing.push('Name');
    if (parsed.lead_email) { updates.lead_email = parsed.lead_email; filled.push('Email'); }
    if (parsed.lead_company) { updates.lead_company = parsed.lead_company; filled.push('Company'); }
    if (parsed.lead_title) { updates.lead_title = parsed.lead_title; filled.push('Title'); }
    if (parsed.lead_location) { updates.lead_location = parsed.lead_location; filled.push('Location'); }
    if (parsed.lead_phone) { updates.lead_phone = parsed.lead_phone; filled.push('Phone'); }
    if (parsed.conversation_raw) { updates.conversation_raw = parsed.conversation_raw; filled.push('Conversation'); }
    else missing.push('Conversation');
    if (dripifyLinkedInUrl.trim()) { updates.lead_linkedin_url = dripifyLinkedInUrl.trim(); filled.push('LinkedIn URL'); }
    if (parsed.alias_profile) { updates.alias_profile = parsed.alias_profile; filled.push('Alias'); }
    else missing.push('Alias (select manually)');

    // Match campaign via dripify_names array
    let matchedCampaignId: string | undefined;
    if (parsed.dripify_campaign) {
      const match = campaigns.find(c => c.dripify_names?.includes(parsed.dripify_campaign!));
      if (match) {
        updates.campaign_id = match.id;
        matchedCampaignId = match.id;
        filled.push(`Campaign (via ${parsed.dripify_campaign} → ${match.name})`);
      } else {
        missing.push(`Campaign "${parsed.dripify_campaign}" not in dripify_names — select manually`);
      }
    } else {
      missing.push('Campaign (select manually)');
    }

    setFormData(prev => ({ ...prev, ...updates }));
    if (matchedCampaignId) loadCampaignMessages(matchedCampaignId);
    setDripifyParseResult({ filled, missing });
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.campaign_id) newErrors.campaign_id = 'Campaign is required';
    if (!formData.lead_name.trim()) newErrors.lead_name = 'Lead name is required';
    // LinkedIn URL is now optional (Post-Epoch 22 fix)
    // Option 1: Minimal validation - just check if URL starts with http:// or https://
    if (formData.lead_linkedin_url.trim() && !formData.lead_linkedin_url.startsWith('http')) {
      newErrors.lead_linkedin_url = 'URL must start with http:// or https://';
    }
    // Lead company is now optional (Post-Epoch 22 fix)
    // if (!formData.lead_company.trim()) newErrors.lead_company = 'Company is required';
    if (!formData.alias_profile.trim()) newErrors.alias_profile = 'Alias profile is required';
    if (!formData.conversation_raw.trim()) newErrors.conversation_raw = 'Conversation is required';
    // CSR notes are now optional (Post-Epoch 22 fix)
    // if (!formData.csr_notes.trim()) newErrors.csr_notes = 'CSR notes are required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setAutoSaveStatus('unsaved');

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const saveDraft = useCallback(async () => {
    setAutoSaveStatus('saving');
    try {
      // Save to localStorage under a key scoped to this specific lead (parentResponse.id) in
      // follow-up mode, so this draft can never be restored into a different lead's form.
      localStorage.setItem(getDraftStorageKey(mode, parentResponse?.id), JSON.stringify(formData));
      setTimeout(() => {
        setAutoSaveStatus('saved');
      }, 1000);
    } catch (error) {
      console.error('Error saving draft:', error);
      setAutoSaveStatus('unsaved');
    }
  }, [formData, mode, parentResponse?.id]);

  // Load campaigns and team members for dropdown and validation
  // EPOCH 51: Warm up Edge Function to prevent cold start timeouts
  const warmUpEdgeFunction = async () => {
    try {
      console.log('🔥 EPOCH 51: Warming up AI parsing Edge Function...');
      // Make a lightweight call with minimal data - using a more realistic warm-up
      const warmUpData = {
        conversation_raw: 'Warm-up message from system\nWarm-up response from test',
        lead_name: 'System Warmup',
        alias_name: 'System',
        analysis_type: 'basic_parse'
      };
      
      const startTime = Date.now();
      const { data, error } = await supabase.functions.invoke('ai-conversation-analysis-openai', {
        body: warmUpData
      });
      
      const duration = Date.now() - startTime;
      console.log(`✅ EPOCH 51: Edge Function warmed up in ${duration}ms`, { data, error });
    } catch (error) {
      // Log but don't fail - this is just a warm-up
      console.warn('⚠️ EPOCH 51: Edge Function warm-up failed (non-critical):', error);
    }
  };

  useEffect(() => {
    loadCampaigns();
    loadRecentCampaigns(); // EPOCH 60: Load recently used campaigns
    loadTeamMembers(); // EPOCH 21 PHASE 3: Load team members for parsing validation
    warmUpEdgeFunction(); // EPOCH 51: Prevent cold start timeouts
    // EPOCH 70: Pre-load messages for follow-up mode
    if (mode === 'followup' && parentResponse?.campaign_id) {
      loadCampaignMessages(parentResponse.campaign_id);
    }
    // Load the real message-limit config for the approaching-limit warning below
    if (mode === 'followup' && parentResponse?.company_id) {
      loadMessagingConfig(parentResponse.company_id);
    }
  }, []);

  // Auto-save draft every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      if (autoSaveStatus === 'unsaved') {
        saveDraft();
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [autoSaveStatus, saveDraft]);

  // Load draft on component mount (or when the follow-up target lead changes). Scoped to
  // this specific parentResponse.id in follow-up mode -- never reads the old unscoped key, and
  // proactively clears it so no other code path can restore a cross-lead draft from it either.
  useEffect(() => {
    try {
      if (mode === 'followup') {
        localStorage.removeItem(LEGACY_UNSCOPED_FOLLOWUP_DRAFT_KEY);
      }
      const draft = localStorage.getItem(getDraftStorageKey(mode, parentResponse?.id));
      if (draft) {
        const draftData = JSON.parse(draft);
        setFormData(draftData);
      }
    } catch (error) {
      console.error('Error loading draft:', error);
    }
  }, [mode, parentResponse?.id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    console.log('🚀 EPOCH 51: Starting lead response submission...');
    
    try {
      // EPOCH 29: Include parent_response_id for follow-ups
      // EPOCH 51: Include parsing validation data if available
      const submissionData = {
        ...formData,
        ...(mode === 'followup' && parentResponse ? { parent_response_id: parentResponse.id } : {}),
        ...(parsedMessages.length > 0 ? {
          parsing_validation_data: {
            parsed_messages: parsedMessages,
            corrections: validationCorrections,
            validated_by_ai: true,
            alias_used: formData.alias_profile
          }
        } : {}),
        // EPOCH 70: Analytics tracking fields
        ...(sequenceMessageNumber !== undefined ? { sequence_message_number: sequenceMessageNumber } : {}),
        response_trigger: responseTrigger
      };
      
      console.log('📦 EPOCH 51: Submission data:', {
        hasParsingData: !!submissionData.parsing_validation_data,
        messageCount: parsedMessages.length,
        correctionCount: validationCorrections.length,
        mode: mode,
        campaignId: submissionData.campaign_id,
        leadName: submissionData.lead_name,
        dataSize: JSON.stringify(submissionData).length
      });

      console.log('⏳ EPOCH 51: Calling lead-responses Edge Function...');
      const startTime = Date.now();
      
      const { data, error } = await supabase.functions.invoke('lead-responses', {
        body: {
          action: 'create',
          data: submissionData
        }
      });
      
      const duration = Date.now() - startTime;
      console.log(`✅ EPOCH 51: Edge Function responded in ${duration}ms`, { data, error });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      // Clear draft on successful submission
      localStorage.removeItem(getDraftStorageKey(mode, parentResponse?.id));
      
      onSuccess?.(data.data);
      
      // Reset form
      setFormData({
        campaign_id: '',
        lead_name: '',
        lead_linkedin_url: '',
        lead_company: '',
        lead_title: '',
        lead_location: '',
        alias_profile: '',
        conversation_raw: '',
        csr_notes: '',
        // Post-Epoch 22: Additional Contact Fields
        lead_email: '',
        lead_phone: '',
        lead_website: ''
      });
      setAutoSaveStatus('saved');
      // EPOCH 70: Reset analytics fields
      setSequenceMessageNumber(undefined);
      setResponseTrigger('drip_sequence');
      setCampaignMessages([]);

    } catch (error) {
      console.error('Error creating lead response:', error);
      const message = await extractEdgeFunctionErrorMessage(
        error,
        'Failed to submit lead response. Please try again.'
      );
      setErrors({ submit: message });
    } finally {
      setLoading(false);
    }
  };

  const renderLinkedInButton = () => {
    // Show button if URL exists and starts with http
    if (!formData.lead_linkedin_url || !formData.lead_linkedin_url.startsWith('http')) {
      return null;
    }

    return (
      <a
        href={formData.lead_linkedin_url}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center px-3 py-1 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 transition-colors"
      >
        <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 24 24">
          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
        </svg>
        View LinkedIn Profile
      </a>
    );
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">
            {mode === 'followup' ? 'Add Follow-up Response' : 'Submit Lead Response'}
          </h2>
          <div className="flex items-center space-x-4">
            <span className={`text-sm ${
              autoSaveStatus === 'saved' ? 'text-green-600' : 
              autoSaveStatus === 'saving' ? 'text-yellow-600' : 'text-gray-600'
            }`}>
              {autoSaveStatus === 'saved' && '✓ Draft saved'}
              {autoSaveStatus === 'saving' && '⏳ Saving...'}
              {autoSaveStatus === 'unsaved' && '• Unsaved changes'}
            </span>
          </div>
        </div>

        {/* EPOCH 29: Parent Response Context for Follow-ups */}
        {mode === 'followup' && parentResponse && (
          <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <h3 className="font-medium text-gray-700 mb-2 flex items-center">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Previous Conversation Details
            </h3>
            <div className="text-sm text-gray-600 space-y-1">
              <p><strong>Lead:</strong> {parentResponse.lead_name}</p>
              <p><strong>Company:</strong> {parentResponse.lead_company || 'Not specified'}</p>
              <p><strong>Campaign:</strong> {parentResponse.campaign?.name}</p>
              <p><strong>Version:</strong> {parentResponse.conversation_version || 1} → {(parentResponse.conversation_version || 1) + 1} (this will be version {(parentResponse.conversation_version || 1) + 1})</p>
              <p><strong>Messages:</strong> {parentResponse.message_count || 2} messages</p>
            </div>
            
            {/* Message limit warning if applicable -- uses the real per-company config
                (company_messaging_config), not a hardcoded value, so it matches whatever the
                backend actually enforces (lead-responses/index.ts's own maxTotalMessages /
                warningThreshold) instead of silently disagreeing with it. */}
            {(parentResponse.message_count || 2) >= (messagingConfig?.warning_at_message_count ?? 8) && (
              <div className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded">
                <p className="text-sm text-yellow-800 flex items-center">
                  <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  Approaching message limit ({(messagingConfig?.max_message_exchanges ?? 5) * 2} messages maximum)
                </p>
              </div>
            )}
          </div>
        )}

        {/* Dripify Smart Paste — only shown for new responses */}
        {mode === 'new' && (
          <div className="mb-6 border border-indigo-200 rounded-lg bg-indigo-50">
            <button
              type="button"
              onClick={() => setShowDripifyPanel(p => !p)}
              className="w-full flex justify-between items-center px-4 py-3 text-left"
            >
              <span className="font-medium text-indigo-700 text-sm">⚡ Quick Fill from Dripify</span>
              <span className="text-indigo-500 text-xs">{showDripifyPanel ? '▲ collapse' : '▼ expand'}</span>
            </button>
            {showDripifyPanel && (
              <div className="px-4 pb-4 space-y-3">
                <p className="text-xs text-indigo-600">
                  Open the lead in Dripify, select all (Ctrl+A), copy, then paste below. All fields auto-fill.
                </p>
                <textarea
                  value={dripifyPaste}
                  onChange={e => setDripifyPaste(e.target.value)}
                  rows={5}
                  className="w-full px-3 py-2 border border-indigo-300 rounded-md text-xs font-mono bg-white focus:outline-none focus:ring-2 focus:ring-indigo-400"
                  placeholder="Paste Dripify lead page here..."
                />
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    LinkedIn URL <span className="text-gray-400">(right-click "Go to lead's page" → Copy link)</span>
                  </label>
                  <input
                    type="url"
                    value={dripifyLinkedInUrl}
                    onChange={e => setDripifyLinkedInUrl(e.target.value)}
                    className="w-full px-3 py-2 border border-indigo-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-400"
                    placeholder="https://linkedin.com/in/username"
                  />
                </div>
                <button
                  type="button"
                  onClick={handleDripifyParse}
                  disabled={!dripifyPaste.trim()}
                  className="px-4 py-2 bg-indigo-600 text-white text-sm rounded-md hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Parse &amp; Fill Fields
                </button>
                {dripifyParseResult && (
                  <div className="text-xs space-y-1 pt-1">
                    {dripifyParseResult.filled.length > 0 && (
                      <p className="text-green-700">✓ Filled: {dripifyParseResult.filled.join(' · ')}</p>
                    )}
                    {dripifyParseResult.missing.length > 0 && (
                      <p className="text-amber-600">⚠ Needs manual input: {dripifyParseResult.missing.join(' · ')}</p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Campaign Dropdown - Read-only for follow-ups */}
          {mode === 'new' ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Campaign *
              </label>
              {/* EPOCH 75: Replaced native <select> with searchable CampaignSearchSelect */}
              <CampaignSearchSelect
                campaigns={campaigns}
                value={formData.campaign_id}
                onChange={(campaignId) => {
                  handleInputChange('campaign_id', campaignId);
                  if (campaignId) loadCampaignMessages(campaignId);
                }}
                placeholder="Select a campaign"
                recentCampaignIds={recentCampaignIds}
                allowEmpty={false}
                className={errors.campaign_id ? 'ring-1 ring-red-500 rounded-lg' : ''}
              />
              {errors.campaign_id && (
                <p className="mt-1 text-sm text-red-600">{errors.campaign_id}</p>
              )}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Campaign
                </label>
                <p className="text-gray-900">{parentResponse?.campaign?.name}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Alias Profile
                </label>
                <p className="text-gray-900">{formData.alias_profile}</p>
              </div>
            </div>
          )}

          {/* EPOCH 70: Message Number & Response Trigger */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* Message Number Dropdown - shows when campaign has sequence messages */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Lead responded after message # <span className="text-gray-500">(Optional)</span>
              </label>
              <select
                value={sequenceMessageNumber ?? ''}
                onChange={(e) => setSequenceMessageNumber(e.target.value ? parseInt(e.target.value) : undefined)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={campaignMessages.length === 0}
              >
                <option value="">{campaignMessages.length === 0 ? 'Select a campaign first' : 'Unknown / not sure'}</option>
                {campaignMessages.map(msg => (
                  <option key={msg.message_number} value={msg.message_number}>
                    Message {msg.message_number} - {msg.content.substring(0, 50)}{msg.content.length > 50 ? '...' : ''}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-sm text-gray-500">
                Which message in the drip sequence did this lead respond to?
              </p>
            </div>

            {/* Response Trigger Dropdown */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Response trigger
              </label>
              <select
                value={responseTrigger}
                onChange={(e) => setResponseTrigger(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="drip_sequence">Drip Sequence</option>
                <option value="reply_request">Reply Request</option>
                <option value="sequence_continuation">Sequence Continuation</option>
              </select>
              <p className="mt-1 text-sm text-gray-500">
                {mode === 'followup' ? 'Auto-detected from reply history. Override if incorrect.' : 'What prompted the lead to respond?'}
              </p>
            </div>
          </div>

          {/* Lead Information Grid */}
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Lead Name *
              </label>
              <input
                type="text"
                value={formData.lead_name}
                onChange={(e) => handleInputChange('lead_name', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.lead_name ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter lead's full name"
                required
              />
              {errors.lead_name && (
                <p className="mt-1 text-sm text-red-600">{errors.lead_name}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Lead Company <span className="text-gray-500">(Optional)</span>
              </label>
              <input
                type="text"
                value={formData.lead_company}
                onChange={(e) => handleInputChange('lead_company', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.lead_company ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter company name"
              />
              {errors.lead_company && (
                <p className="mt-1 text-sm text-red-600">{errors.lead_company}</p>
              )}
            </div>
          </div>

          {/* LinkedIn URL with button display */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Lead LinkedIn URL <span className="text-gray-500">(Optional)</span>
            </label>
            <input
              type="url"
              value={formData.lead_linkedin_url}
              onChange={(e) => handleInputChange('lead_linkedin_url', e.target.value)}
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.lead_linkedin_url ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="https://linkedin.com/in/username"
            />
            {errors.lead_linkedin_url && (
              <p className="mt-1 text-sm text-red-600">{errors.lead_linkedin_url}</p>
            )}
            {renderLinkedInButton() && (
              <div className="mt-2">
                {renderLinkedInButton()}
              </div>
            )}
          </div>

          {/* Optional Fields Grid */}
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Lead Title <span className="text-gray-500">(Optional)</span>
              </label>
              <input
                type="text"
                value={formData.lead_title}
                onChange={(e) => handleInputChange('lead_title', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., Marketing Director"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Lead Location <span className="text-gray-500">(Optional)</span>
              </label>
              <input
                type="text"
                value={formData.lead_location}
                onChange={(e) => handleInputChange('lead_location', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., San Francisco, CA"
              />
            </div>
          </div>

          {/* Post-Epoch 22: Additional Contact Fields */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Lead Email <span className="text-gray-500">(Optional)</span>
              </label>
              <input
                type="email"
                value={formData.lead_email || ''}
                onChange={(e) => handleInputChange('lead_email', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., john@company.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Lead Phone <span className="text-gray-500">(Optional)</span>
              </label>
              <input
                type="tel"
                value={formData.lead_phone || ''}
                onChange={(e) => handleInputChange('lead_phone', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., +1 (555) 123-4567"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Lead Website <span className="text-gray-500">(Optional)</span>
              </label>
              <input
                type="url"
                value={formData.lead_website || ''}
                onChange={(e) => handleInputChange('lead_website', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., https://company.com"
              />
            </div>
          </div>

          {/* Alias Profile - Show for all modes */}
          {/* EPOCH 51: Changed from text input to dropdown - available for all response types */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Alias Profile *
            </label>
            <select
              value={formData.alias_profile}
              onChange={(e) => handleInputChange('alias_profile', e.target.value)}
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.alias_profile ? 'border-red-500' : 'border-gray-300'
              }`}
              required
              disabled={loadingAliases}
            >
              <option value="">Select an alias...</option>
              {aliasOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {errors.alias_profile && (
              <p className="mt-1 text-sm text-red-600">{errors.alias_profile}</p>
            )}
            <p className="mt-1 text-sm text-gray-500">
              The LinkedIn profile/alias you used to send the original message to this lead.
            </p>
          </div>

          {/* Conversation Thread */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {mode === 'followup' ? 'Complete Updated Conversation *' : 'Conversation Thread *'}
            </label>
            <textarea
              value={formData.conversation_raw}
              onChange={(e) => handleInputChange('conversation_raw', e.target.value)}
              rows={10}
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.conversation_raw ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder={mode === 'followup' 
                ? 'Copy and paste the ENTIRE conversation thread from LinkedIn, including all previous messages and the new follow-up...'
                : 'Copy and paste the LinkedIn conversation here...'}
              required
            />
            {errors.conversation_raw && (
              <p className="mt-1 text-sm text-red-600">{errors.conversation_raw}</p>
            )}
            <p className="mt-1 text-sm text-gray-500">
              {mode === 'followup' 
                ? 'Copy the ENTIRE conversation thread from LinkedIn, including all previous messages and the new follow-up. Include timestamps and sender names.'
                : 'Copy the entire conversation thread from LinkedIn and paste it here. Include timestamps and sender names.'}
            </p>
            
            {/* EPOCH 21 PHASE 3: Parsing Validation Controls */}
            {formData.conversation_raw.trim() && (
              <div className="mt-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <label className="flex items-center text-sm text-gray-600">
                      <input
                        type="checkbox"
                        checked={showParsingPreview}
                        onChange={(e) => setShowParsingPreview(e.target.checked)}
                        className="mr-2"
                      />
                      Show parsing preview
                    </label>
                    
                    {parsedMessages.length > 0 && (
                      <div className="text-sm text-gray-600">
                        {parsedMessages.length} messages detected
                        {validationCorrections.length > 0 && (
                          <span className="ml-2 text-blue-600">
                            ({validationCorrections.length} corrections applied)
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  
                  <button
                    type="button"
                    onClick={() => setShowValidationInterface(true)}
                    className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                  >
                    Advanced Validation
                  </button>
                </div>
                
                {/* Live Parsing Preview */}
                {showParsingPreview && (
                  <ParsingPreviewPanel
                    rawConversation={formData.conversation_raw}
                    teamMembers={teamMembers}
                    onMessagesChanged={handleParsingPreviewUpdate}
                    showConfidenceMetrics={true}
                    enableLivePreview={false}
                    validatedMessages={parsedMessages.length > 0 ? parsedMessages : undefined}
                  />
                )}
              </div>
            )}
          </div>

          {/* CSR Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              CSR Notes <span className="text-gray-500">(Optional)</span>
            </label>
            <textarea
              value={formData.csr_notes}
              onChange={(e) => handleInputChange('csr_notes', e.target.value)}
              rows={4}
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.csr_notes ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Add any relevant notes or context for the user..."
            />
            {errors.csr_notes && (
              <p className="mt-1 text-sm text-red-600">{errors.csr_notes}</p>
            )}
            <p className="mt-1 text-sm text-gray-500">
              Provide context about the lead's response, interest level, or any important details.
            </p>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-4 pt-6 border-t">
            {onCancel && (
              <button
                type="button"
                onClick={onCancel}
                className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
            )}
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? (mode === 'followup' ? 'Adding Follow-up...' : 'Submitting...') : (mode === 'followup' ? 'Add Follow-up' : 'Submit Lead Response')}
            </button>
          </div>

          {errors.submit && (
            <p className="text-sm text-red-600 text-center">{errors.submit}</p>
          )}
        </form>
      </div>
      
      {/* EPOCH 21 PHASE 3 & 51: Advanced Validation Interface Modal with alias */}
      {showValidationInterface && (
        <CSRParsingValidationInterface
          rawConversation={formData.conversation_raw}
          leadName={formData.lead_name || 'Lead'}
          aliasName={formData.alias_profile} // EPOCH 51: Pass alias name
          onParsingValidated={handleParsingValidated}
          onClose={() => setShowValidationInterface(false)}
          showInModal={true}
        />
      )}
    </div>
  );
};

export default CSRLeadResponseForm;